import { ReactNode } from 'react';
import styled from '@emotion/styled';
import Card from './Card';
import Title from './Title';
import Description from './Description';
import IconBadge from './IconBadge';
import CountBadge from './CountBadge';
import Cta from './Cta';
import { settingsTheme as t } from '../theme';

export interface SettingsCardProps {
  /** Icono de la sección. */
  icon: ReactNode;
  iconBg?: string;
  iconColor?: string;
  title: string;
  description: string;
  /** Texto de la llamada a la acción ("Administrar", "Próximamente"...). */
  ctaLabel: string;
  /** Conteo opcional; si llega un número se pinta la píldora. */
  count?: number;
  /** Atenúa la tarjeta y desactiva la navegación para secciones aún no disponibles. */
  disabled?: boolean;
  onClick?: () => void;
}

const Layout = styled(Card)({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  minHeight: 184,
});

const Header = styled.div({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
});

const Body = styled.div({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: 7,
});

/**
 * Molécula central del hub. Ensambla los primitivos `Card`, `IconBadge`,
 * `CountBadge`, `Title`, `Description` y `Cta` en la tarjeta de una sección de
 * configuración. No decide a dónde navega: solo emite `onClick`.
 */
const SettingsCard = ({
  icon,
  iconBg,
  iconColor,
  title,
  description,
  ctaLabel,
  count,
  disabled = false,
  onClick,
}: SettingsCardProps) => (
  <Layout interactive={!disabled} muted={disabled} onClick={disabled ? undefined : onClick}>
    <Header>
      <IconBadge
        icon={icon}
        bg={disabled ? t.color.disabledIconBg : iconBg}
        color={disabled ? t.color.disabledIcon : iconColor}
      />
      {typeof count === 'number' ? <CountBadge>{count}</CountBadge> : null}
    </Header>
    <Body>
      <Title size="card" muted={disabled}>
        {title}
      </Title>
      <Description size="card" muted={disabled}>
        {description}
      </Description>
    </Body>
    <Cta label={ctaLabel} color={disabled ? t.color.disabled : iconColor} />
  </Layout>
);

export default SettingsCard;
